import { useState, useEffect, useContext, useCallback } from "react";
import styled from "styled-components";
import NavbarAdmin from "../../Components/Admin/NavbarAdmin";
import Sidebar from "../../Components/Admin/Sidebar";
import logo from "../../Assets/logo.png";
import { IoIosArrowBack } from "react-icons/io";
import { MdDelete } from "react-icons/md";
import io from "socket.io-client";
import axios from "axios";
import { AuthContext } from "../../hooks/useLogin";

const socket = io("https://inaaniserver-50c4305cc7f7.herokuapp.com");

const Messages = () => {
  const { user } = useContext(AuthContext); // Get user from AuthContext
  const [selectedChat, setSelectedChat] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [hoveredMessage, setHoveredMessage] = useState(null);
  const [showDelete, setShowDelete] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  // Use userId from AuthContext (admin's ID)
  const userId = user?.id;

  // adminId will be set based on the selected conversation (recipient's ID)
  const [adminId, setAdminId] = useState(null);

  const fetchConversations = useCallback(async () => {
    if (!userId) return; // Prevent fetches if userId is not available
    try {
      const response = await axios.get(`https://inaaniserver-50c4305cc7f7.herokuapp.com/inbox/conversation/${userId}`);
      console.log("Fetched conversations:", response.data);
      const formattedConversations = response.data.map((conv) => {
        console.log("Raw conversation:", conv);
        const name =
          conv.user1_id === userId
            ? conv.user2_fullname || `User ${conv.user2_id || "Unknown"}`
            : conv.user1_fullname || `User ${conv.user1_id || "Unknown"}`;
        const preview = conv.latest_message || "No messages yet";
        const unread_count = Number(conv.unread_count) || 0;

        // Determine recipientId (adminId) for this conversation
        const recipientId = conv.user1_id === userId ? conv.user2_id : conv.user1_id;

        // Calculate recency
        const transdate = new Date(conv.transdate);
        const now = new Date();
        const diffMs = now - transdate;
        const diffMins = Math.floor(diffMs / (1000 * 60));
        let recency;
        if (diffMins < 1) {
          recency = "Just now";
        } else if (diffMins < 60) {
          recency = `${diffMins}m`;
        } else if (diffMins < 1440) {
          const diffHours = Math.floor(diffMins / 60);
          recency = `${diffHours}h`;
        } else {
          const diffDays = Math.floor(diffMins / 1440);
          recency = `${diffDays}d`;
        }

        return {
          conversation_id: conv.conversation_id,
          name,
          preview,
          unread_count,
          recency,
          recipientId, // Store recipientId for later use
        };
      });
      setConversations(formattedConversations);
    } catch (error) {
      console.error("Error fetching conversations:", error.response?.data || error.message);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      console.log("Current userId:", userId);
      fetchConversations();
    }
  }, [userId, fetchConversations]);

  useEffect(() => {
    if (!userId) return;
    socket.emit("join", userId);
    console.log(`User ${userId} joined Socket.IO room`);

    socket.on(`message_${userId}`, (msgData) => {
      console.log("Received socket.io message:", msgData);
      if (msgData.conversation_id === selectedChat?.conversation_id) {
        setMessages((prevMessages) => [...prevMessages, msgData]);
      }
      fetchConversations();
    });

    return () => {
      socket.off(`message_${userId}`);
    };
  }, [selectedChat, userId, fetchConversations]);

  const fetchMessages = async (conversationId) => {
    try {
      const response = await axios.get(`https://inaaniserver-50c4305cc7f7.herokuapp.com/inbox/messages/${conversationId}`);
      console.log("Fetched messages (raw response):", response.data);
      setMessages(response.data);
      console.log("Updated messages state:", response.data);
      console.log("Messages state length:", response.data.length);
      console.log("First message (if any):", response.data[0]);
    } catch (error) {
      console.error("Error fetching messages:", error.response?.data || error.message);
    }
  };

  const handleSelectChat = async (conversation) => {
    console.log("Selected chat:", conversation);
    setSelectedChat(conversation);
    setAdminId(conversation.recipientId); // Set adminId based on the conversation's recipient

    try {
      // Fetch messages first
      await fetchMessages(conversation.conversation_id);

      // Then mark all unread messages as read
      if (conversation.unread_count > 0) {
        const unreadMessages = messages.filter(
          (msg) => msg.received_id === userId && msg.is_read === 0
        );

        for (const msg of unreadMessages) {
          await markAsRead(msg.id);
        }

        // Refresh conversations to update unread count
        fetchConversations();
      }
    } catch (error) {
      console.error("Error handling chat selection:", error);
    }
  };

  const sendMessage = async () => {
    if (newMessage.trim() === "" || !adminId) return;

    try {
      const messageData = {
        user_id: userId,
        admin_id: adminId,
        message: newMessage,
      };

      const response = await axios.post("https://inaaniserver-50c4305cc7f7.herokuapp.com/inbox/send", messageData);
      console.log("Message sent:", response.data);

      // Clear the input
      setNewMessage("");
      setShowDelete(null);

      // Refresh conversations to update the latest_message
      fetchConversations();
    } catch (error) {
      console.error("Error sending message:", error.response?.data || error.message);
    }
  };

  const markAsRead = async (messageId) => {
    try {
      await axios.put(`https://inaaniserver-50c4305cc7f7.herokuapp.com/inbox/read/${messageId}`);
      setMessages((prevMessages) =>
        prevMessages.map((msg) =>
          msg.id === messageId ? { ...msg, is_read: 1 } : msg
        )
      );
      fetchConversations();
    } catch (error) {
      console.error("Error marking message as read:", error.response?.data || error.message);
    }
  };

  const deleteMessage = async (messageId, index) => {
    try {
      await axios.delete(`https://inaaniserver-50c4305cc7f7.herokuapp.com/inbox/delete/${messageId}`);
      setMessages((prevMessages) => prevMessages.filter((_, i) => i !== index));
    } catch (error) {
      console.error("Error deleting message:", error.response?.data || error.message);
    }
  };

  // Prevent rendering if user is not logged in
  if (!userId) {
    return <div>Please log in to view messages.</div>;
  }

  return (
    <MainContainer>
      <NavbarAdmin />
      <ContentContainer>
        <Sidebar
          isOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
        />
        <InboxContainer $sidebarOpen={isSidebarOpen}>
          <MessagesList>
            <SidebarHeader>Messages</SidebarHeader>
            {conversations.length > 0 ? (
              conversations.map((conv) => (
                <MessageItem
                  key={conv.conversation_id}
                  onClick={() => handleSelectChat(conv)}
                >
                  <Avatar>
                    <img src={logo} alt={conv.name} />
                  </Avatar>
                  <MessagePreview>
                    <strong>{conv.name}</strong>
                    <span>{conv.preview}</span>
                    <Timestamp>{conv.recency}</Timestamp>
                    {conv.unread_count > 0 && <UnreadDot />}
                  </MessagePreview>
                </MessageItem>
              ))
            ) : (
              <NoConversations>
                No conversations yet
                <StartConversationButton
                  onClick={() => console.log("Start a new conversation")}
                >
                  Start a new conversation
                </StartConversationButton>
              </NoConversations>
            )}
          </MessagesList>

          {selectedChat ? (
            <ChatInterface>
              <ChatHeader>
                <BackIcon onClick={() => setSelectedChat(null)}>
                  <IoIosArrowBack />
                </BackIcon>
                <ChatTitle>{selectedChat.name}</ChatTitle>
                <HeaderOptions>...</HeaderOptions>
              </ChatHeader>

              <ChatBody>
                {Array.isArray(messages) && messages.length > 0 ? (
                  messages.map((msg, index) => (
                    <ChatMessage
                      key={msg.id || index}
                      isUser={msg.sender_id === userId}
                      onMouseEnter={() => setHoveredMessage(index)}
                      onMouseLeave={() => setHoveredMessage(null)}
                    >
                      <Avatar>
                        <img src={logo} alt="User" />
                      </Avatar>
                      <MessageBubble $isUser={msg.sender_id === userId}>
                        {msg.message}
                        <MessageTimestamp $isUser={msg.sender_id === userId}>
                          {new Date(msg.transdate).toLocaleTimeString([], {
                            hour: "numeric",
                            minute: "2-digit",
                          })}
                        </MessageTimestamp>
                        {msg.is_read === 0 && msg.sender_id === userId && (
                          <ReadStatus $isUser={msg.sender_id === userId}>
                            Unread
                          </ReadStatus>
                        )}
                      </MessageBubble>
                      {msg.sender_id === userId && (
                        <MessageOptions
                          onClick={() =>
                            setShowDelete(showDelete === index ? null : index)
                          }
                          style={{
                            visibility:
                              hoveredMessage === index ? "visible" : "hidden",
                          }}
                        >
                          ⋮
                        </MessageOptions>
                      )}
                      {showDelete === index && (
                        <DeleteButton
                          onClick={() => deleteMessage(msg.id, index)}
                        >
                          <MdDelete />
                        </DeleteButton>
                      )}
                    </ChatMessage>
                  ))
                ) : (
                  <div>No messages to display</div>
                )}
              </ChatBody>

              <ChatInputContainer>
                <ChatInput
                  type="text"
                  placeholder="Send message..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && sendMessage()}
                />
                <SendButton onClick={sendMessage}>Send</SendButton>
              </ChatInputContainer>
            </ChatInterface>
          ) : (
            <NoChatSelected>Select a conversation to start chatting</NoChatSelected>
          )}
        </InboxContainer>
      </ContentContainer>
    </MainContainer>
  );
};

export default Messages;

// Styled components
const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

const ContentContainer = styled.div`
  display: flex;
  flex: 1;
`;

const InboxContainer = styled.div`
  display: flex;
  flex: 1;
  height: calc(100vh - 80px);
  font-family: Arial, sans-serif;
  margin-left: ${({ $sidebarOpen }) => ($sidebarOpen ? "200px" : "50px")};
  transition: margin-left 0.5s ease;
`;

const NoConversations = styled.div`
  text-align: center;
  color: #888888;
  padding: 1rem;
`;

const SidebarHeader = styled.h2`
  font-size: 1.5rem;
  color: #004d00;
  margin: 0 0 1rem 0;
  padding: 0.5rem;
  text-align: center;
  background-color: #f0f0f0;
  border-bottom: 1px solid #e5e5e5;
`;

const MessagesList = styled.div`
  width: 350px;
  border-right: 1px solid #e5e5e5;
  padding: 1rem;
  background-color: #f9f9f9;
  overflow-y: auto;
`;

const MessageItem = styled.div`
  display: flex;
  align-items: center;
  padding: 0.5rem 1rem;
  cursor: pointer;
  border-radius: 8px;
  transition: background-color 0.2s;
  position: relative;

  &:hover {
    background-color: #e0f7e0;
  }
`;

const Avatar = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  overflow: hidden;
  margin-right: 1rem;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const MessagePreview = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  font-size: 0.9rem;
  position: relative;

  strong {
    font-size: 1.1rem;
    color: #004d00;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 200px;
  }

  span {
    color: #888888;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 200px;
  }
`;

const ChatInterface = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
`;

const ChatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  background-color: #f0f0f0;
  border-bottom: 1px solid #e5e5e5;
`;

const BackIcon = styled.div`
  cursor: pointer;
  font-size: 1.5rem;
  color: #004d00;
`;

const ChatTitle = styled.h2`
  font-size: 1.2rem;
  color: #004d00;
  margin: 0;
`;

const HeaderOptions = styled.div`
  cursor: pointer;
  font-size: 1rem;
  color: #004d00;
`;

const ChatBody = styled.div`
  flex: 1;
  padding: 1rem;
  overflow-y: auto;
  background-color: #f9f9f9;
  display: flex;
  flex-direction: column;
`;

const ChatMessage = styled.div.withConfig({
  shouldForwardProp: (prop) => !["isUser"].includes(prop),
})`
  display: flex;
  align-items: flex-start;
  margin-bottom: 1rem;
  align-self: ${(props) => (props.isUser ? "flex-end" : "flex-start")};
  cursor: pointer;
`;

const MessageBubble = styled.div.withConfig({
  shouldForwardProp: (prop) => !["isUser"].includes(prop),
})`
  background: ${(props) => (props.isUser ? "#004d00" : "#e0ffe0")};
  color: ${(props) => (props.isUser ? "white" : "#004d00")};
  padding: 10px 15px;
  border-radius: 15px;
  max-width: 60%;
  word-wrap: break-word;
  position: relative;
  margin-left: ${(props) => (props.isUser ? "auto" : "0")};
  margin-right: ${(props) => (props.isUser ? "0" : "auto")};
`;

const MessageTimestamp = styled.span.withConfig({
  shouldForwardProp: (prop) => !["isUser"].includes(prop),
})`
  font-size: 0.75rem;
  color: ${(props) => (props.isUser ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 77, 0, 0.7)")};
  display: block;
  margin-top: 5px;
  text-align: ${(props) => (props.isUser ? "right" : "left")};
`;

const ReadStatus = styled.span.withConfig({
  shouldForwardProp: (prop) => !["isUser"].includes(prop),
})`
  font-size: 0.65rem;
  color: ${(props) => (props.isUser ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 77, 0, 0.5)")};
  display: block;
  margin-top: 2px;
  text-align: ${(props) => (props.isUser ? "right" : "left")};
`;

const ChatInputContainer = styled.div`
  padding: 1rem;
  border-top: 1px solid #e5e5e5;
  background-color: #ffffff;
  display: flex;
  justify-content: space-between;
`;

const ChatInput = styled.input`
  width: 85%;
  padding: 0.8rem;
  border: 1px solid #cccccc;
  border-radius: 20px;
  font-size: 1rem;
  outline: none;

  &:focus {
    border-color: rgb(22, 173, 22);
  }
`;

const SendButton = styled.button`
  padding: 0.8rem 1.5rem;
  border: none;
  background-color: #004d00;
  color: #ffffff;
  border-radius: 20px;
  cursor: pointer;
  font-size: 1rem;

  &:hover {
    background-color: #003300;
  }
`;

const DeleteButton = styled.button`
  background: none;
  border: none;
  color: red;
  cursor: pointer;
  font-size: 1.2rem;
  margin-left: 10px;
  display: flex;
  align-items: center;

  &:hover {
    color: darkred;
  }
`;

const MessageOptions = styled.div`
  cursor: pointer;
  font-size: 1.2rem;
  color: #888;
  margin-left: 8px;
  display: flex;
  align-items: center;
  visibility: hidden;

  &:hover {
    color: #004d00;
  }
`;

const NoChatSelected = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1.2rem;
  color: #888888;
  background-color: #f9f9f9;
`;

const UnreadDot = styled.div`
  width: 10px;
  height: 10px;
  background-color: #1a73e8;
  border-radius: 50%;
  position: absolute;
  right: 10px;
  bottom: 10px;
`;

const StartConversationButton = styled.button`
  margin-top: 1rem;
  padding: 0.5rem 1rem;
  background-color: #004d00;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;

  &:hover {
    background-color: #003300;
  }
`;

const Timestamp = styled.div`
  position: absolute;
  right: 0;
  top: 0.5rem;
  font-size: 0.8rem;
  color: #888888;
`;